import { useCallback, useEffect, useState } from 'react';
import { knowledgeApi } from '../../supportApi';
import { getDiscussionPreferencesCached } from '../cache/discussionPreferencesCache';

const LOCAL_NAV_COLLAPSED_KEY = 'od_shell_nav_collapsed_v1';

function readLocalNavCollapsed(tenantId, userId) {
  if (typeof window === 'undefined' || !userId || !tenantId) return false;
  try {
    return localStorage.getItem(`${LOCAL_NAV_COLLAPSED_KEY}:${tenantId}:${userId}`) === '1';
  } catch {
    return false;
  }
}

function writeLocalNavCollapsed(tenantId, userId, collapsed) {
  if (typeof window === 'undefined' || !userId || !tenantId) return;
  try {
    localStorage.setItem(`${LOCAL_NAV_COLLAPSED_KEY}:${tenantId}:${userId}`, collapsed ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/**
 * Loads and persists posts shell left-nav collapse per signed-in user in the database.
 * Renders immediately from localStorage, then syncs from the server in the background.
 */
export function usePostsShellNavCollapsed(userId, profileResolved = true, tenantId = '') {
  const [isNavCollapsed, setIsNavCollapsed] = useState(() => readLocalNavCollapsed(tenantId, userId));

  useEffect(() => {
    if (!profileResolved) return undefined;
    if (!userId || !tenantId) {
      setIsNavCollapsed(false);
      return undefined;
    }
    setIsNavCollapsed(readLocalNavCollapsed(tenantId, userId));
    let cancelled = false;
    (async () => {
      try {
        const data = await getDiscussionPreferencesCached(userId, tenantId);
        if (cancelled) return;
        const next = data?.shellNavCollapsed === true;
        setIsNavCollapsed(next);
        writeLocalNavCollapsed(tenantId, userId, next);
      } catch {
        /* keep local default */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, tenantId, profileResolved]);

  const persistNavCollapsed = useCallback(
    (collapsed) => {
      if (!userId || !tenantId) return;
      writeLocalNavCollapsed(tenantId, userId, collapsed);
      knowledgeApi.discussionsPreferencesSet({ shellNavCollapsed: collapsed }).catch(() => {});
    },
    [userId, tenantId]
  );

  const toggleNavCollapsed = useCallback(() => {
    setIsNavCollapsed((prev) => {
      const next = !prev;
      persistNavCollapsed(next);
      return next;
    });
  }, [persistNavCollapsed]);

  const setNavCollapsed = useCallback(
    (collapsed) => {
      const next = Boolean(collapsed);
      setIsNavCollapsed(next);
      persistNavCollapsed(next);
    },
    [persistNavCollapsed]
  );

  return { isNavCollapsed, toggleNavCollapsed, setNavCollapsed, navCollapsedReady: true };
}

/** Shell layout is never blocked on profile or nav prefs (local defaults render immediately). */
export function isPostsShellNavLayoutReady() {
  return true;
}
